import { useEffect, useState } from "react";
import { Button } from "@/components/shared/ui/button";
import { ThemeToggle } from "@/components/shared/ui/theme-toggle";
import { Badge } from "@/components/shared/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadCommentCount } from "@/hooks/useUnreadCommentCount";
import { useTherapistUnreadNotes } from "@/hooks/useTherapistUnreadNotes";
import { useRole } from "@/hooks/useRole";
import {
  LogOut,
  User,
  Settings,
  Menu,
  BarChart3,
  Users,
  BookOpen,
  Bell,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/shared/ui/sheet";
import useIsAdmin from "@/hooks/useIsAdmin";
import { NotificationDrawer } from "@/components/shared/notifications/NotificationDrawer";

type NavigationProps = {
  // When true, forces a minimal public header even without an authenticated profile
  publicView?: boolean;
};

export const Navigation = ({
  publicView: _publicView = false,
}: NavigationProps) => {
  const { profile, signOut } = useAuth();
  const { label: roleLabel } = useRole();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const isAuthenticated = !!profile;

  // Determine admin access via server-side check (SECURITY DEFINER RPC)
  const { isAdmin } = useIsAdmin();

  // Get unread counts based on role
  const { unreadCount } = useUnreadCommentCount();
  const { unreadNotesCount } = useTherapistUnreadNotes();

  const menuItems =
    profile?.role === "therapist"
      ? [
          {
            label: t("navigation.journeyBuilder", "Journey builder"),
            href: "/dashboard/therapist/journey-builder",
            icon: BarChart3,
          },
          {
            label: t("navigation.contentLibrary", "Content library"),
            href: "/dashboard/therapist/content",
            icon: BookOpen,
          },
          {
            label: t("navigation.clientManagement"),
            href: "/dashboard/therapist/clients",
            icon: Users,
            badge: unreadNotesCount > 0 ? unreadNotesCount : undefined,
          },
          /*    {
            label: t("navigation.analytics"),
            href: "/dashboard/therapist/security-audit",
            icon: BarChart3,
          }, */
          ...(isAdmin
            ? [
                {
                  label: t("navigation.adminPanel"),
                  href: "/admin",
                  icon: Settings,
                },
              ]
            : []),
        ]
      : [
          {
            label: t("navigation.overview", "Overview"),
            href: "/dashboard/client/journey",
            icon: BarChart3,
            badge: unreadCount > 0 ? unreadCount : undefined,
          },
          {
            label: t("journey.pastJourneys"),
            href: "/dashboard/client/journey/history",
            icon: BookOpen,
          },
        ];

  useEffect(() => {
    const handleOpenMenu = () => setIsMenuOpen(true);
    window.addEventListener("open-nav-menu", handleOpenMenu);
    return () => window.removeEventListener("open-nav-menu", handleOpenMenu);
  }, []);

  return (
    <nav
      className="bg-background pt-safe-top"
      role="navigation"
      aria-label="Main"
    >
      <div className="container mx-auto px-4 sm:px-6 px-safe py-4">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:rounded"
        >
          {t("common.tilToppen", "Til toppen")}
        </a>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Link to="/" className="hover:opacity-90 transition-opacity">
              <h1 className="text-lg sm:text-xl font-semibold truncate">
                {t("navigation.title")}
              </h1>
            </Link>
            {isAuthenticated && profile?.role === "therapist" && (
              <span className="hidden sm:inline text-sm text-muted-foreground">
                • {t("navigation.therapistRole")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* Desktop Navigation */}
            {isAuthenticated && (
              <div className="hidden md:flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="max-w-[200px] truncate">
                  {profile?.full_name || profile?.email}
                </span>
              </div>
            )}

            <div className="hidden md:flex items-center gap-4">
              {/* Notification Bell */}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNotificationDrawerOpen(true)}
                  className="relative min-touch-target"
                  aria-label={t(
                    "notifications.notificationDrawerTitle",
                    "Notifikationer"
                  )}
                >
                  <Bell className="h-5 w-5" />
                  {((profile?.role === "client" && unreadCount > 0) ||
                    (profile?.role === "therapist" &&
                      unreadNotesCount > 0)) && (
                    <Badge
                      variant="notification"
                      data-testid="notification-badge"
                      className="absolute -top-1 -right-1 px-1.5 min-w-[1.25rem] h-5"
                    >
                      {profile?.role === "client"
                        ? unreadCount
                        : unreadNotesCount}
                    </Badge>
                  )}
                </Button>
              )}

              {/* <LanguageSelector /> */}
              <ThemeToggle />
              {/*  {isAuthenticated && isAdmin && (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/admin">
                    <Settings className="h-4 w-4 mr-2" />
                    <span data-testid="nav-admin">
                      {t("navigation.adminPanel")}
                    </span>
                  </Link>
                </Button>
              )} */}

              {/*   {isAuthenticated ? (
                <>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/profile">
                      <Settings className="h-4 w-4 mr-2" />
                      <span data-testid="nav-profile">
                        {t("navigation.profile")}
                      </span>
                    </Link>
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">{t("common.logIn")}</Link>
                </Button>
              )}
 */}
              {/* Rightmost CTA only when NOT authenticated */}
              {/*{!isAuthenticated && (
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  asChild
                >
                  <Link to="/#beta">{t("landing.beta.ctaPrimary")}</Link>
                </Button>
              )}*/}
            </div>
            {/* Mobile / small-screen Navigation (always use the burger sheet) */}
            {/* Mobile Notification Bell (visible on small screens, next to burger menu) */}
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setNotificationDrawerOpen(true)}
                className="md:hidden relative min-touch-target mr-2"
                aria-label={t(
                  "notifications.notificationDrawerTitle",
                  "Notifikationer"
                )}
                data-testid="nav-notification-mobile"
              >
                <Bell className="h-5 w-5" />
                {((profile?.role === "client" && unreadCount > 0) ||
                  (profile?.role === "therapist" && unreadNotesCount > 0)) && (
                  <Badge
                    variant="notification"
                    data-testid="notification-badge-mobile"
                    className="absolute -top-1 -right-1 px-1.5 min-w-[1.25rem] h-5"
                  >
                    {profile?.role === "client"
                      ? unreadCount
                      : unreadNotesCount}
                  </Badge>
                )}
              </Button>
            )}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-touch-target"
                  aria-label="Navigation menu"
                  data-testid="nav-menu-mobile"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[90vw] max-w-sm pb-safe-bottom px-safe !px-6"
              >
                <div className="flex flex-col space-y-4 mt-6">
                  {isAuthenticated ? (
                    <div className="flex items-center gap-2 text-sm pb-4">
                      <User className="h-4 w-4 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-sm">
                          {profile?.full_name || profile?.email}
                        </p>
                        <p className="text-muted-foreground capitalize">
                          {roleLabel ?? profile?.role}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="pb-2">
                      <p className="text-sm text-muted-foreground">
                        {t("navigation.title")}
                      </p>
                    </div>
                  )}

                  {/* Theme toggle visible inside sheet for all users */}
                  <div className="flex items-center justify-end">
                    <ThemeToggle />
                  </div>

                  {isAuthenticated && profile?.role === "therapist" && (
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t("navigation.therapistTools", "Therapist tools")}
                      </p>
                      {menuItems.map((item) => (
                        <Button
                          key={item.href}
                          variant="ghost"
                          className="justify-start min-touch-target"
                          role="menuitem"
                          asChild
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Link
                            to={item.href}
                            data-testid={`nav-${item.href.split("/").filter(Boolean).join("-")}`}
                          >
                            <item.icon className="h-4 w-4 mr-3" />
                            {item.label}
                          </Link>
                        </Button>
                      ))}
                    </div>
                  )}

                  {isAuthenticated &&
                    profile?.role !== "therapist" &&
                    menuItems.map((item) => (
                      <Button
                        key={item.href}
                        variant="ghost"
                        className="justify-start min-touch-target"
                        role="menuitem"
                        asChild
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Link
                          to={item.href}
                          data-testid={`nav-${item.href.split("/").filter(Boolean).join("-")}`}
                        >
                          <item.icon className="h-4 w-4 mr-3" />
                          {item.label}
                        </Link>
                      </Button>
                    ))}

                  {!isAuthenticated && (
                    <>
                      <Button
                        variant="ghost"
                        className="justify-start min-touch-target"
                        role="menuitem"
                        asChild
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Link to="/login" data-testid="nav-login-mobile">
                          {t("common.logIn")}
                        </Link>
                      </Button>
                    </>
                  )}

                  {isAuthenticated && (
                    <div className="pt-4 space-y-3 border-t border-border/60">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Settings className="h-4 w-4" />
                          <span>{t("navigation.settings", "Settings")}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="ghost"
                          className="justify-start min-touch-target"
                          asChild
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Link to="/profile">
                            <Settings className="h-4 w-4 mr-3" />
                            {t("navigation.profile")}
                          </Link>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {isAuthenticated && (
                  <div className="pt-3 border-t border-border/60">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsMenuOpen(false);
                        signOut();
                      }}
                      role="menuitem"
                      aria-label={t("navigation.signOut")}
                      className="justify-start min-touch-target w-full"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      {t("navigation.signOut")}
                    </Button>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Notification Drawer */}
      {isAuthenticated && profile && (
        <NotificationDrawer
          open={notificationDrawerOpen}
          onOpenChange={setNotificationDrawerOpen}
          userRole={profile.role}
        />
      )}
    </nav>
  );
};
